/**
 * Selbstbedienung für Browser-Benachrichtigungen (Profilseite).
 *
 * Zeigt den Stand für DIESES Gerät - nicht für das Konto. Wer am Laptop und am
 * Handy angemeldet ist, muss die Erlaubnis zweimal erteilen; das ist keine
 * Einschränkung der Anwendung, sondern wie die Push-API funktioniert, und die
 * Oberfläche sagt es deshalb ausdrücklich.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BellIcon, BellSlashIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { pushStatus, pushAktivieren, pushDeaktivieren, pushTesten, PushStatus } from '../utils/pushNotifications';

const PushNotificationSettings: React.FC = () => {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<{ text: string; art: 'ok' | 'fehler' } | null>(null);

  const statusLaden = useCallback(async () => {
    setStatus(await pushStatus());
  }, []);

  useEffect(() => {
    statusLaden();
  }, [statusLaden]);

  const umschalten = async () => {
    setLaeuft(true);
    setMeldung(null);
    try {
      if (status?.abonniert) {
        await pushDeaktivieren();
        setMeldung({ text: 'Benachrichtigungen für dieses Gerät ausgeschaltet.', art: 'ok' });
      } else {
        await pushAktivieren();
        setMeldung({ text: 'Benachrichtigungen sind aktiv.', art: 'ok' });
      }
      await statusLaden();
    } catch (err: any) {
      setMeldung({ text: err?.message || 'Das hat nicht geklappt.', art: 'fehler' });
    } finally {
      setLaeuft(false);
    }
  };

  const testen = async () => {
    setLaeuft(true);
    setMeldung(null);
    try {
      const zugestellt = await pushTesten();
      setMeldung(
        zugestellt > 0
          ? { text: `Probenachricht an ${zugestellt} Gerät(e) geschickt.`, art: 'ok' }
          : { text: 'Es war kein Gerät erreichbar. Vielleicht wurde die Erlaubnis entzogen?', art: 'fehler' }
      );
      await statusLaden();
    } catch {
      setMeldung({ text: 'Die Probenachricht konnte nicht verschickt werden.', art: 'fehler' });
    } finally {
      setLaeuft(false);
    }
  };

  if (!status) return null;

  const blockiert = status.erlaubnis === 'denied';

  /**
   * Zeigt den Stand der drei Voraussetzungen.
   *
   * Erscheint nur, wenn etwas davon im Weg ist. Läuft alles, bleibt die Karte
   * aufgeräumt - aber im Fehlerfall steht der Grund auf dem Bildschirm, statt
   * dass jemand die Entwicklerkonsole öffnen muss.
   */
  const Diagnose = () => (
    <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-3 text-xs text-gray-600 dark:text-gray-400">
      <p className="font-medium text-gray-700 dark:text-gray-300">Was gerade fehlt</p>
      <ul className="mt-1.5 space-y-0.5">
        <li>Browser-Erlaubnis: <strong>{
          status.erlaubnis === 'granted' ? 'erteilt'
            : status.erlaubnis === 'denied' ? 'blockiert'
            : status.erlaubnis === 'default' ? 'noch nicht gefragt'
            : 'nicht verfügbar'
        }</strong></li>
        <li>Service Worker: <strong>{status.serviceWorker ? 'registriert' : 'nicht registriert'}</strong></li>
        <li>Server: <strong>{status.konfiguriert ? 'eingerichtet' : 'nicht eingerichtet'}</strong> ({status.apiBasis})</li>
      </ul>
      {status.grund && <p className="mt-2 text-gray-700 dark:text-gray-300">{status.grund}</p>}
    </div>
  );

  // Ohne Browser-Unterstützung oder ohne Server-Schlüssel wäre jeder Schalter
  // eine Lüge - dann nur den Grund nennen.
  if (!status.unterstuetzt || !status.konfiguriert) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-6 sm:p-7">
        <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
          <BellIcon className="h-5 w-5 mr-2 opacity-80" /> Benachrichtigungen
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Benachrichtigungen sind hier gerade nicht möglich.
        </p>
        <Diagnose />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-6 sm:p-7">
      <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
        <BellIcon className="h-5 w-5 mr-2 opacity-80" /> Benachrichtigungen
      </h3>

      <p className="text-sm text-gray-700 dark:text-gray-300">
        Erinnerung an deine gebuchten Termine, am Vortag um 18 Uhr. Eine Nachricht je Tag,
        mit allen Terminen des nächsten Tages.
      </p>
      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        Die Erlaubnis gilt nur für dieses Gerät und diesen Browser. Auf dem iPhone funktioniert
        es nur, wenn die Seite zuvor über „Teilen → Zum Home-Bildschirm" hinzugefügt wurde.
      </p>

      {blockiert && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-warning-50 dark:bg-warning-900/20 p-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-warning-600 dark:text-warning-400" />
          <p className="text-sm text-warning-800 dark:text-warning-200">
            Dieser Browser blockiert Benachrichtigungen für die Seite. Das lässt sich nur in den
            Browser-Einstellungen wieder ändern, nicht hier.
          </p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={umschalten}
          disabled={laeuft || blockiert}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
            status.abonniert ? 'bg-gray-600 hover:bg-gray-700' : 'bg-primary-600 hover:bg-primary-700'
          }`}
        >
          {status.abonniert ? <BellSlashIcon className="h-5 w-5" /> : <BellIcon className="h-5 w-5" />}
          {status.abonniert ? 'Ausschalten' : 'Einschalten'}
        </button>

        {status.abonniert && (
          <button
            type="button"
            onClick={testen}
            disabled={laeuft}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50"
          >
            Probenachricht
          </button>
        )}

        {status.abonniert && (
          <span className="inline-flex items-center gap-1.5 text-sm text-success-700 dark:text-success-400">
            <CheckCircleIcon className="h-5 w-5" /> Für dieses Gerät aktiv
          </span>
        )}
      </div>

      {!status.abonniert && <Diagnose />}

      {meldung && (
        <p
          className={`mt-3 text-sm ${
            meldung.art === 'ok'
              ? 'text-success-700 dark:text-success-400'
              : 'text-error-600 dark:text-error-400'
          }`}
        >
          {meldung.text}
        </p>
      )}
    </div>
  );
};

export default PushNotificationSettings;
